import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import CheckboxWithLabel from "~/molecules/CheckboxWithLabel";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import DatePicker from "~/molecules/DatePicker";
import type { DocumentFiltersModel } from "~/hooks/useDocumentFilters";
import {
  FilterOperatorEnum,
  fromDateKey,
  isNullableDateFilterKey,
  type MultiSelectFilterKey,
  type NullableDateFilterKey,
  toDateKey,
} from "~/helpers/documentFilters";
import { FiltersPanel } from "./FiltersPanel";

interface DocumentFiltersPanelProps {
  filters: DocumentFiltersModel;
  showDrafts?: boolean;
  onShowDraftsChange?: (value: boolean) => void;
}

const getNullFilterLabels = (
  t: TFunction,
): Record<NullableDateFilterKey, string> => ({
  adoptedAt: t(($) => $.library.filters.nullLabel.adoptedAt),
  reviewFrom: t(($) => $.library.filters.nullLabel.reviewFrom),
  reviewTo: t(($) => $.library.filters.nullLabel.reviewTo),
});

export const DocumentFiltersPanel = ({
  filters,
  showDrafts,
  onShowDraftsChange,
}: DocumentFiltersPanelProps) => {
  const { t } = useTranslation();
  const nullFilterLabels = getNullFilterLabels(t);
  const getMultiSelectOptions = (field: MultiSelectFilterKey) => {
    const { values, labelsByValue } = filters.multiSelectFieldData[field];
    return values.map((value) => ({
      value,
      label: labelsByValue[value] ?? value,
    }));
  };

  const getMultiSelectPlaceholder = (
    selectedCount: number,
    fallbackPlaceholder: string,
  ) =>
    selectedCount === 0
      ? fallbackPlaceholder
      : t(($) => $.library.filters.selectedCount, {
          count: selectedCount,
        });

  return (
    <FiltersPanel>
      {showDrafts !== undefined && onShowDraftsChange && (
        <div className="space-y-1">
          <p className="text-sm font-medium">
            {t(($) => $.commons.document.status)}
          </p>

          <CheckboxWithLabel
            id="show-drafts"
            label={t(($) => $.chart.includeDrafts)}
            checked={showDrafts}
            onCheckedChange={onShowDraftsChange}
          />
        </div>
      )}

      {filters.multiSelectFilterKeys.map((field) => {
        const selectedValues = filters.getInValues(field);

        return (
          <div key={field} className="space-y-1">
            <p className="text-sm font-medium">
              {t(($) => $.commons.document[field])}
            </p>
            <ControlledCombobox
              isMulti
              value={selectedValues}
              options={getMultiSelectOptions(field)}
              placeholder={getMultiSelectPlaceholder(
                selectedValues.length,
                t(($) => $.commons.select),
              )}
              onChange={(values) => filters.setInValues(field, values)}
            />
          </div>
        );
      })}

      {filters.dateFilterKeys.map((filterKey) => {
        const nullableKey = isNullableDateFilterKey(filterKey)
          ? filterKey
          : undefined;
        const nullActive =
          nullableKey !== undefined && filters.isNullFilterActive(nullableKey);

        return (
          <div key={filterKey} className="space-y-1">
            <p className="text-sm font-medium">
              {t(($) => $.commons.document[filterKey])}
            </p>
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-[3.5rem_1fr] items-center gap-2">
                <span className="text-sm">
                  {t(($) => $.library.filters.after)}
                </span>
                <DatePicker
                  disabled={nullActive}
                  value={fromDateKey(
                    filters.getFilterValue(filterKey, FilterOperatorEnum.gte),
                  )}
                  onChange={(date) =>
                    filters.setFilterValue(
                      filterKey,
                      FilterOperatorEnum.gte,
                      date ? (toDateKey(date) ?? undefined) : undefined,
                    )
                  }
                />
              </div>
              <div className="grid grid-cols-[3.5rem_1fr] items-center gap-2">
                <span className="text-sm">
                  {t(($) => $.library.filters.before)}
                </span>
                <DatePicker
                  disabled={nullActive}
                  value={fromDateKey(
                    filters.getFilterValue(filterKey, FilterOperatorEnum.lte),
                  )}
                  onChange={(date) =>
                    filters.setFilterValue(
                      filterKey,
                      FilterOperatorEnum.lte,
                      date ? (toDateKey(date) ?? undefined) : undefined,
                    )
                  }
                />
              </div>
              {filters.isDateRangeInvalid(filterKey) && (
                <p className="text-destructive text-sm">
                  {t(($) => $.library.filters.invalidDateRange)}
                </p>
              )}
              {nullableKey !== undefined && (
                <CheckboxWithLabel
                  id={`${nullableKey}-null`}
                  label={nullFilterLabels[nullableKey]}
                  checked={filters.isNullFilterActive(nullableKey)}
                  onCheckedChange={(checked) =>
                    filters.setNullFilter(nullableKey, checked)
                  }
                />
              )}
            </div>
          </div>
        );
      })}
    </FiltersPanel>
  );
};
